import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiGet, apiPost } from '../../../services/api';
import { authApiGet, authApiPost } from '../../../services/authApi';
import {
  UsersResponseSchema,
  UserDetailResponseSchema,
  type User,
  type UsersResponse,
  type UserDetailResponse,
  type UserFilters,
} from '../types/user.types';
import {
  AdminBulkResolveResponseSchema,
  AdminUserSearchResponseSchema,
  AdminUserResponseSchema,
} from '../types/auth-service.types';

// Helper function to convert snake_case API response to camelCase
function toCamelCase(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj !== null && typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        result[camelKey] = toCamelCase((obj as Record<string, unknown>)[key]);
        return result;
      },
      {} as Record<string, unknown>
    );
  }
  return obj;
}

// --- Main API response shapes (business data only, post PII-isolation) ---

interface MainApiUserListItem {
  id: string;
  createdAt: string;
  updatedAt?: string;
  subscriptionStatus?: string;
  subscriptionPlan?: string | null;
}

interface MainApiUserListResponse {
  items: MainApiUserListItem[];
  total: number;
}

interface MainApiUserDetail {
  id: string;
  nativeLanguage?: string | null;
  onboardingCompleted: boolean;
  onboardingCompletedAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  subscriptionStatus?: string;
  subscriptionPlan?: string | null;
  subscriptionPeriodEnd?: string | null;
  subscriptionCancelAtPeriodEnd?: boolean;
  subscriptionTrialUsed?: boolean;
  subscriptionStore?: string | null;
}

// --- Fetcher functions ---

/**
 * Default list (no search): main API for business data + auth service bulk resolve for PII.
 * Role/status filters applied client-side on merged results.
 */
const fetchUsersDefault = async (filters: UserFilters): Promise<UsersResponse> => {
  const params: Record<string, string | number> = {};

  if (filters.sortOrder) {
    params.sort_order = filters.sortOrder;
  }
  if (filters.page !== undefined) {
    params.offset = ((filters.page || 1) - 1) * (filters.limit || 50);
  }
  if (filters.limit) {
    params.limit = filters.limit;
  }

  // 1. Get business data from main API
  const mainData = toCamelCase(
    await apiGet<unknown>('/api/admin/users', params)
  ) as MainApiUserListResponse;

  // 2. Get PII from auth service bulk resolve
  const userIds = mainData.items.map((item) => item.id);

  let piiMap: Record<
    string,
    { email: string; name?: string | null; role: string; isActive: boolean; isVerified: boolean }
  > = {};
  if (userIds.length > 0) {
    const bulkData = toCamelCase(
      await authApiGet<unknown>('/api/admin/users/bulk', { ids: userIds.join(',') })
    );
    const parsed = AdminBulkResolveResponseSchema.parse(bulkData);
    piiMap = parsed.users;
  }

  // 3. Merge main API business data with auth service PII
  //    Skip users without auth records (orphaned profiles)
  const mergedItems: User[] = mainData.items
    .filter((item) => piiMap[item.id])
    .map((item) => {
      const pii = piiMap[item.id];
      return {
        id: item.id,
        email: pii.email,
        role: pii.role as 'user' | 'admin',
        isActive: pii.isActive,
        isVerified: pii.isVerified,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        subscriptionStatus: (item.subscriptionStatus as User['subscriptionStatus']) ?? 'none',
        subscriptionPlan: (item.subscriptionPlan as User['subscriptionPlan']) ?? null,
      };
    });

  // 4. Apply client-side role/status filters on merged results
  let filteredItems = mergedItems;
  if (filters.role && filters.role !== 'all') {
    filteredItems = filteredItems.filter((u) => u.role === filters.role);
  }
  if (filters.status && filters.status !== 'all') {
    const isActive = filters.status === 'active';
    filteredItems = filteredItems.filter((u) => u.isActive === isActive);
  }

  return UsersResponseSchema.parse({
    items: filteredItems,
    total: mainData.total,
  });
};

/**
 * Search mode: auth service search endpoint handles PII search + role/status filtering.
 * Subscription data not available in search results (shows defaults).
 */
const fetchUsersWithSearch = async (filters: UserFilters): Promise<UsersResponse> => {
  const params: Record<string, string | number | boolean> = {
    q: filters.search!,
  };

  if (filters.role && filters.role !== 'all') {
    params.role = filters.role;
  }
  if (filters.status && filters.status !== 'all') {
    params.is_active = filters.status === 'active';
  }
  if (filters.page !== undefined) {
    params.offset = ((filters.page || 1) - 1) * (filters.limit || 50);
  }
  if (filters.limit) {
    params.limit = filters.limit;
  }

  const searchData = toCamelCase(await authApiGet<unknown>('/api/admin/users/search', params));
  const parsed = AdminUserSearchResponseSchema.parse(searchData);

  // Map auth service results to User type (no subscription data in search mode)
  const items: User[] = parsed.users.map((authUser) => ({
    id: authUser.id,
    email: authUser.email,
    role: authUser.role,
    isActive: authUser.isActive,
    isVerified: authUser.isVerified,
    createdAt: authUser.createdAt,
    subscriptionStatus: 'none' as const,
    subscriptionPlan: null,
  }));

  return UsersResponseSchema.parse({
    items,
    total: parsed.total,
  });
};

/**
 * Fetch users: delegates to search or default list based on filters.
 */
const fetchUsers = async (filters: UserFilters = {}): Promise<UsersResponse> => {
  if (filters.search && filters.search.length >= 3) {
    return fetchUsersWithSearch(filters);
  }
  return fetchUsersDefault(filters);
};

/**
 * Fetch user detail: parallel calls to auth service (PII) + main API (business data), merged.
 */
const fetchUser = async (userId: string): Promise<UserDetailResponse> => {
  const [authData, mainData] = await Promise.all([
    authApiGet<unknown>(`/api/admin/users/${userId}`).then((data) =>
      AdminUserResponseSchema.parse(toCamelCase(data))
    ),
    apiGet<unknown>(`/api/admin/users/${userId}`).then(
      (data) => toCamelCase(data) as MainApiUserDetail
    ),
  ]);

  const merged = {
    id: authData.id,
    email: authData.email,
    role: authData.role,
    isActive: authData.isActive,
    isVerified: authData.isVerified,
    createdAt: authData.createdAt,
    updatedAt: mainData.updatedAt,
    nativeLanguage: mainData.nativeLanguage,
    onboardingCompleted: mainData.onboardingCompleted,
    onboardingCompletedAt: mainData.onboardingCompletedAt,
    subscriptionStatus: mainData.subscriptionStatus ?? 'none',
    subscriptionPlan: mainData.subscriptionPlan ?? null,
    subscriptionPeriodEnd: mainData.subscriptionPeriodEnd ?? null,
    subscriptionCancelAtPeriodEnd: mainData.subscriptionCancelAtPeriodEnd ?? false,
    subscriptionTrialUsed: mainData.subscriptionTrialUsed ?? false,
    subscriptionStore: mainData.subscriptionStore ?? null,
  };

  return UserDetailResponseSchema.parse(merged);
};

/**
 * Block user — calls auth service.
 */
const blockUser = async (userId: string): Promise<void> => {
  const data = await authApiPost<unknown>(`/api/admin/users/${userId}/block`);
  AdminUserResponseSchema.parse(toCamelCase(data));
};

/**
 * Unblock user — calls auth service.
 */
const unblockUser = async (userId: string): Promise<void> => {
  const data = await authApiPost<unknown>(`/api/admin/users/${userId}/unblock`);
  AdminUserResponseSchema.parse(toCamelCase(data));
};

/**
 * Invalidate user tokens (force logout) — calls auth service.
 */
const invalidateUserTokens = async (userId: string): Promise<void> => {
  const data = await authApiPost<unknown>(`/api/admin/users/${userId}/invalidate-tokens`);
  AdminUserResponseSchema.parse(toCamelCase(data));
};

// React Query hooks
export const useUsers = (filters: UserFilters = {}): UseQueryResult<UsersResponse, Error> => {
  return useQuery({
    queryKey: ['users', filters],
    queryFn: () => fetchUsers(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUser = (userId: string): UseQueryResult<UserDetailResponse, Error> => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useBlockUser = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: blockUser,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
};

export const useUnblockUser = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unblockUser,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
};

export const useInvalidateUserTokens = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invalidateUserTokens,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
};

/**
 * Terminate user subscription immediately — calls main API.
 */
const terminateSubscription = async (userId: string): Promise<void> => {
  await apiPost(`/api/admin/users/${userId}/subscription/terminate`);
};

/**
 * Extend user subscription — calls main API.
 */
const extendSubscription = async ({
  userId,
  days,
}: {
  userId: string;
  days: number;
}): Promise<void> => {
  await apiPost(`/api/admin/users/${userId}/subscription/extend`, { days });
};

export const useTerminateSubscription = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: terminateSubscription,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
};

export const useExtendSubscription = (): UseMutationResult<
  void,
  Error,
  { userId: string; days: number }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: extendSubscription,
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
};

// Export types
export type { User, UsersResponse, UserFilters };
